/**
 * Pool saturation accounting — "is the bottleneck full, and who is waiting?".
 *
 * The expensive pools are deliberately small (spec §4.1), so the signal that says
 * a cap is too low is not CPU or memory but *queueing*: how long a caller waits
 * for a slot, how deep the queue gets, and how long the pool spends starved.
 *
 * Two properties of the scheduler make this cheap and exact:
 *
 *  - **A parked waiter *is* saturation.** `acquireScheduledWorker` parks a caller
 *    only when no child is idle *and* the pool cannot spawn (`size` reached), so the
 *    queue only ever becomes non-empty once the pool has genuinely run out of
 *    capacity — never while a slot is free. A non-empty queue is therefore spec §6's
 *    starvation ("all slots busy **and** a non-empty queue"), distinct from
 *    merely-busy, and no separate capacity predicate is needed.
 *
 *    The one place the two readings diverge: `retire()` drops a child from
 *    `children` and respawns asynchronously, so while a replacement is starting the
 *    queue can be non-empty with `children.size < size`. That window counts as
 *    starved **deliberately** — a caller is queued and cannot be served, which is
 *    precisely what this measures. Reading it the other way (require
 *    `children.size === size`) would report *zero* starvation exactly when the pool
 *    is most degraded. It does mean a kill storm inflates `timeAtCapacityMs` without
 *    the cap being too low, so read this against `kills`, which names that case.
 *  - **Only parked waits are timed.** A caller that spawns its own child returns from
 *    `acquireScheduledWorker` before the park branch, so it is never timed and pool
 *    warmup is not charged to the caller that triggered it. That also makes "no
 *    samples" a meaningful reading ("nothing ever queued") rather than a gap.
 *
 *    What this measures is therefore **time-to-slot for callers that had to queue** —
 *    not pure queueing. A child is added to `children` synchronously at spawn *start*
 *    (see `spawn-lifecycle.ts`), so it holds a slot while it readies and later callers
 *    park behind it: their samples include that peer's spawn+handshake (up to
 *    `readyTimeoutMs`). Same for a respawn — one crash with a caller queued posts a
 *    sample covering the whole replacement. Those callers really did wait that long, so
 *    the number is honest; it just is not evidence on its own that the *cap* is too
 *    low. Read it against `kills`, which distinguishes churn from cap pressure.
 *
 * Accounting is pure arithmetic (one clock read per parked acquire), not `/proc`
 * reads, so unlike the sibling {@link ./resource-sampler.js} it needs no knob and is
 * always armed. Counters are cumulative — like `kills` and `rssHighWaterBytes` — so
 * {@link WorkerPoolSaturationTracker.stats} stays non-destructive for its several
 * callers; take the rate downstream.
 *
 * @module worker-pool/saturation
 */

import { rankSorted, type ResourcePercentiles } from './resource-sampler.js';

/**
 * Cap on retained wait samples — a rolling window, newest kept, matching the
 * sibling resource sampler's window. The percentiles below describe this window;
 * `waitSampleCount` is the lifetime total, so the two answer different questions
 * ("how long do waits run lately?" vs "how often did anything queue at all?").
 */
const MAX_WAIT_SAMPLES = 256;

/** Wait-for-lease distribution. p99 rides along with the sampler's p50/p95/max. */
export interface WaitPercentiles extends ResourcePercentiles {
  p99: number;
}

export interface WorkerPoolSaturationStats {
  /**
   * Wait-for-lease latency over the retained window. Present only once a caller
   * has actually queued — an uncontended pool reports no distribution at all
   * rather than a misleading row of zeros (the `resourceStats` convention).
   */
  waitMs?: WaitPercentiles;
  /** Lifetime count of callers that parked in the queue. 0 ⇒ never contended. */
  waitSampleCount: number;
  /** Deepest the waiter queue has ever been (lifetime max). */
  queueDepthHighWater: number;
  /**
   * Lifetime milliseconds spent with a non-empty queue — i.e. starved, not merely
   * busy. Cumulative and monotonic; take the rate to get "fraction of the window
   * at capacity".
   */
  timeAtCapacityMs: number;
}

/**
 * p50/p95/p99/max of `nums`. Sorts once and derives all four off the shared
 * nearest-rank primitive (the sibling `percentiles()` carries no p99).
 */
const waitPercentiles = (nums: number[]): WaitPercentiles => {
  const sorted = [...nums].sort((a, b) => a - b);
  return {
    p50: rankSorted(sorted, 0.5),
    p95: rankSorted(sorted, 0.95),
    p99: rankSorted(sorted, 0.99),
    max: sorted[sorted.length - 1] ?? 0,
  };
};

/**
 * Accumulates one pool's queueing signal. Fed by the scheduler's three waiter-queue
 * mutations (park / hand-off / drain) — the only places `waiters` changes.
 */
export class WorkerPoolSaturationTracker {
  private readonly waitSamples: number[] = [];
  private waitSampleCount = 0;
  private queueDepthHighWater = 0;
  private timeAtCapacityMs = 0;
  /** Start of the current starved interval; undefined while the queue is empty. */
  private atCapacitySince: number | undefined;
  private readonly now: () => number;

  constructor(opts: { now?: () => number } = {}) {
    this.now = opts.now ?? Date.now;
  }

  /**
   * A caller parked in the queue. `queueDepth` is the depth *including* this
   * caller. Returns the enqueue timestamp to hand back on {@link dequeued}.
   */
  enqueued(queueDepth: number): number {
    const at = this.now();
    if (queueDepth > this.queueDepthHighWater) this.queueDepthHighWater = queueDepth;
    this.atCapacitySince ??= at;
    return at;
  }

  /**
   * A parked caller left the queue — handed a child, or rejected on drain/respawn
   * failure (a wait that ended in failure is still a wait). `queueDepth` is the
   * depth *after* removal; at 0 the pool is no longer starved. An `enqueuedAt` of
   * `undefined` (a waiter parked without a tracker) records no sample but still
   * closes the interval.
   */
  dequeued(enqueuedAt: number | undefined, queueDepth: number): void {
    const at = this.now();
    if (enqueuedAt !== undefined) this.recordWait(Math.max(0, at - enqueuedAt));
    if (queueDepth === 0) this.closeAtCapacity(at);
  }

  stats(): WorkerPoolSaturationStats {
    // Fold in the open interval without closing it — stats() must not mutate.
    const open = this.atCapacitySince === undefined ? 0 : Math.max(0, this.now() - this.atCapacitySince);
    return {
      ...(this.waitSamples.length > 0 ? { waitMs: waitPercentiles(this.waitSamples) } : {}),
      waitSampleCount: this.waitSampleCount,
      queueDepthHighWater: this.queueDepthHighWater,
      timeAtCapacityMs: this.timeAtCapacityMs + open,
    };
  }

  private recordWait(ms: number): void {
    this.waitSampleCount += 1;
    this.waitSamples.push(ms);
    if (this.waitSamples.length > MAX_WAIT_SAMPLES) this.waitSamples.shift();
  }

  private closeAtCapacity(at: number): void {
    if (this.atCapacitySince === undefined) return;
    this.timeAtCapacityMs += Math.max(0, at - this.atCapacitySince);
    this.atCapacitySince = undefined;
  }
}
